import React, { useEffect, useState } from "react";
import { userAuthHeader } from "../_helpers";
import axios from "axios";
import moment from "moment";
import { getFormJuridique } from "../_helpers";
import Layout from "./Layout";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Alert,
  Tabs,
  Tab,
} from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DownloadIcon from "@mui/icons-material/Download";
import BusinessIcon from "@mui/icons-material/Business";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import ReceiptIcon from "@mui/icons-material/Receipt";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";

// closingDate is stored as YYYY-MM-DD (varchar) but legacy rows may be DD/MM/YYYY.
const parseDate = (value) => {
  if (!value) return null;
  const m = moment(value, ["YYYY-MM-DD", "DD/MM/YYYY", moment.ISO_8601], true);
  return m.isValid() ? m : null;
};
const formatClosingDate = (value) => {
  const m = parseDate(value);
  return m ? m.format("DD/MM/YYYY") : "N/A";
};
const hasValue = (v) => v !== null && v !== undefined && v !== "" && Number(v) !== 0;
const EUR = new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" });
const formatMoney = (v) => EUR.format(parseFloat(v));
const fmtSiren = (v) => {
  if (!v) return "N/A";
  const s = String(v).replace(/\D/g, "");
  return s.length === 9 ? `${s.slice(0, 3)} ${s.slice(3, 6)} ${s.slice(6)}` : s;
};
const FR_DATE = new Intl.DateTimeFormat("fr-FR", { timeZone: "Europe/Paris", day: "2-digit", month: "2-digit", year: "numeric" });
const FR_DATETIME = new Intl.DateTimeFormat("fr-FR", { timeZone: "Europe/Paris", day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
const formatParisDate = (v) => { if (!v) return "N/A"; const d = new Date(v); return isNaN(d) ? "N/A" : FR_DATE.format(d); };
const formatParisDateTime = (v) => { if (!v) return "N/A"; const d = new Date(v); return isNaN(d) ? "N/A" : FR_DATETIME.format(d).replace(",", "").replace(/(\d{4}) /, "$1 à "); };
const FILING_TYPE_LABELS = { annual_accounts: "Comptes annuels", consolidated_accounts: "Comptes consolidés" };
const LIASSE_TYPE_LABELS = { complete_balance_sheet: "Bilan complet", simplified_balance_sheet: "Bilan simplifié", cash_accounts: "Comptes de trésorerie" };
// Labels mirror prod-deposits-accounts/shared/pricing.ts (PRICING_PACKAGES)
const PACKAGE_TYPE_LABELS = {
  standard: "Dépôt simple",
  late: "Dépôt tardif",
  with_generation: "Dépôt + Génération",
  late_with_generation: "Dépôt tardif + Génération",
  monthly: "Forfait mensuel",
};
const DOCUMENT_TYPE_LABELS = {
  meeting_minutes: "Procès-verbal d'approbation des comptes",
  power_attorney_legal_rep: "Procuration du représentant légal",
  privacy_statement: "Déclaration de confidentialité des comptes annuels",
  signed_balance_sheet: "Bilan signé",
  signed_bilan: "Bilan signé",
  synthesis_pj99: "Synthèse PJ99",
  simplified_publication: "Publication simplifiée",
  rejection_details: "Détails du rejet",
};
const labelOf = (map, key) => (key && map[key]) || key || "—";

export default function DepotComptesDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [filing, setFiling] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [documentTab, setDocumentTab] = useState(0);

  useEffect(() => {
    fetchFilingDetails();
  }, [id]);

  const fetchFilingDetails = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}api/user/deposit-accounts/${id}`,
        {
          headers: userAuthHeader(),
        }
      );

      if (response.data && response.data.success) {
        setFiling(response.data.data);
      } else {
        setError("Dépôt de comptes non trouvé");
      }
    } catch (error) {
      console.error("Error fetching filing details:", error);
      if (error.response?.status === 401) {
        setError("Session expirée. Veuillez vous reconnecter.");
      } else {
        setError(
          error.response?.data?.message ||
            "Erreur lors du chargement des détails"
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const statusColors = {
      draft: "default",
      signed: "success",
      submitted: "warning",
      validated: "success",
      rejected: "error",
      pending: "warning",
    };
    return statusColors[status] || "default";
  };

  const getStatusLabel = (status) => {
    const statusLabels = {
      draft: "Brouillon",
      signed: "Signé",
      submitted: "Soumis",
      validated: "Validé",
      rejected: "Rejeté",
      pending: "En attente",
    };
    return statusLabels[status] || status;
  };

  const getPaymentStatusColor = (paymentStatus) => {
    const colors = {
      succeeded: "success",
      monthly: "success",
      pending: "warning",
      failed: "error",
    };
    return colors[paymentStatus] || "default";
  };

  const getPaymentStatusLabel = (paymentStatus) => {
    const labels = {
      succeeded: "Payé",
      monthly: "Forfait mensuel",
      pending: "En attente",
      failed: "Échoué",
    };
    return labels[paymentStatus] || paymentStatus || "En attente";
  };

  if (loading) {
    return (
      <Layout>
        <div className="xl:col-span-9 col-span-12 bg-white rounded-[20px] sideShadow flex items-center justify-center p-3 sm:p-4 md:p-5 lg:p-6" style={{minHeight: '400px'}}>
          <CircularProgress sx={{ color: '#1cbe93' }} />
        </div>
      </Layout>
    );
  }

  if (error || !filing) {
    return (
      <Layout>
        <div className="xl:col-span-9 col-span-12 bg-white rounded-[20px] sideShadow p-3 sm:p-4 md:p-5 lg:p-6">
          <Box className="flex flex-col items-center justify-center" style={{minHeight: '400px'}}>
            <Alert severity="error" className="mb-4 w-full max-w-md">{error || "Dépôt de comptes non trouvé"}</Alert>
            <Box className="flex flex-col sm:flex-row gap-2 mt-4">
              <Button
                variant="outlined"
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate("/prouser/depot-comptes")}
                sx={{
                  borderColor: '#1cbe93',
                  color: '#1cbe93',
                  '&:hover': {
                    borderColor: '#17a37e',
                    backgroundColor: 'rgba(28, 190, 147, 0.04)',
                  },
                }}
              >
                Retour à la liste
              </Button>
              <Button
                variant="contained"
                onClick={fetchFilingDetails}
                sx={{
                  backgroundColor: '#1cbe93',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: '#17a37e',
                  },
                }}
              >
                Réessayer
              </Button>
            </Box>
          </Box>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="xl:col-span-9 col-span-12 bg-white rounded-[20px] sideShadow p-3 sm:p-4 md:p-5 lg:p-6">
        {/* Header */}
        <div className="mb-4 sm:mb-5 md:mb-6">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-4">
            <Typography variant="h4" component="h1" className="font-bold text-xl sm:text-2xl md:text-3xl lg:text-4xl">
              Dépôt de comptes #{filing.id}
            </Typography>
            <Button
              variant="contained"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate("/prouser/depot-comptes")}
              sx={{
                backgroundColor: '#1cbe93',
                color: 'white',
                fontSize: { xs: '0.875rem', sm: '1rem' },
                padding: { xs: '8px 16px', sm: '10px 20px' },
                '&:hover': {
                  backgroundColor: '#17a37e',
                },
              }}
            >
              Retour à la liste
            </Button>
          </div>
        </div>

        {/* Status Cards */}
        <Grid container spacing={2} className="mb-4 sm:mb-5 md:mb-6">
          <Grid item xs={12} md={6}>
            <Card sx={{ height: '100%' }}>
              <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Typography variant="h6" className="mb-3" sx={{ fontSize: { xs: '1rem', sm: '1.1rem', md: '1.25rem' } }}>
                  Statut du dépôt
                </Typography>
                <Chip
                  label={getStatusLabel(filing.status)}
                  color={getStatusColor(filing.status)}
                  size="large"
                  sx={{ mb: 2 }}
                />
                {filing.inpiReferenceNumber && (
                  <Typography variant="body2" className="mt-auto" sx={{ fontSize: { xs: '0.875rem', sm: '0.9rem' } }}>
                    <strong>Référence INPI:</strong>{" "}
                    {filing.inpiReferenceNumber}
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card sx={{ height: '100%' }}>
              <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Typography variant="h6" className="mb-3" sx={{ fontSize: { xs: '1rem', sm: '1.1rem', md: '1.25rem' } }}>
                  Statut du paiement
                </Typography>
                <Chip
                  label={getPaymentStatusLabel(filing.paymentStatus)}
                  color={getPaymentStatusColor(filing.paymentStatus)}
                  size="large"
                  sx={{ mb: 2 }}
                />
                <Box className="mt-auto">
                  {filing.amountPaid && (
                    <Typography variant="body2" sx={{ fontSize: { xs: '0.875rem', sm: '0.9rem' }, mb: 0.5 }}>
                      <strong>Montant&nbsp;:</strong>{" "}
                      {formatMoney(filing.amountPaid)}
                    </Typography>
                  )}
                  {filing.packageType && (
                    <Typography variant="body2" sx={{ fontSize: { xs: '0.875rem', sm: '0.9rem' } }}>
                      <strong>Forfait&nbsp;:</strong> {labelOf(PACKAGE_TYPE_LABELS, filing.packageType)}
                    </Typography>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Company Information */}
        <Card className="mb-4 sm:mb-5 md:mb-6">
          <CardContent sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
            <Typography variant="h6" className="mb-3 sm:mb-4 flex items-center gap-2" sx={{ fontSize: { xs: '1rem', sm: '1.1rem', md: '1.25rem' } }}>
              <BusinessIcon sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }} /> Informations de l'entreprise
            </Typography>
            <Divider className="mb-3 sm:mb-4" />
            <Grid container spacing={{ xs: 2, sm: 2.5 }}>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, mb: 0.5 }}>
                  Nom de l'entreprise
                </Typography>
                <Typography variant="body1" className="font-semibold" sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}>
                  {filing.companyName || "N/A"}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, mb: 0.5 }}>
                  SIREN
                </Typography>
                <Typography variant="body1" className="font-semibold" sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}>
                  {fmtSiren(filing.siren)}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, mb: 0.5 }}>
                  Forme juridique
                </Typography>
                <Typography variant="body1">
                  {getFormJuridique(filing.legalForm) || "N/A"}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="text.secondary">
                  Adresse
                </Typography>
                <Typography variant="body1">
                  {filing.companyAddress || "N/A"}
                  {filing.companyPostalCode && `, ${filing.companyPostalCode}`}
                  {filing.companyCity && ` ${filing.companyCity}`}
                </Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Filing Details */}
        <Card className="mb-4 sm:mb-5 md:mb-6">
          <CardContent sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
            <Typography variant="h6" className="mb-3 sm:mb-4 flex items-center gap-2" sx={{ fontSize: { xs: '1rem', sm: '1.1rem', md: '1.25rem' } }}>
              <ReceiptIcon sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }} /> Détails du dépôt
            </Typography>
            <Divider className="mb-3 sm:mb-4" />
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="text.secondary">
                  Type de dépôt
                </Typography>
                <Typography variant="body1" className="font-semibold">
                  {labelOf(FILING_TYPE_LABELS, filing.filingType)}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="text.secondary">
                  Type de liasse
                </Typography>
                <Typography variant="body1" className="font-semibold">
                  {labelOf(LIASSE_TYPE_LABELS, filing.liasseType)}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="text.secondary">
                  Date de clôture
                </Typography>
                <Typography variant="body1">{formatClosingDate(filing.closingDate)}</Typography>
              </Grid>
              {hasValue(filing.revenue) && (
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary">
                    Chiffre d'affaires
                  </Typography>
                  <Typography variant="body1">{formatMoney(filing.revenue)}</Typography>
                </Grid>
              )}
              {hasValue(filing.totalAssets) && (
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary">
                    Total actif
                  </Typography>
                  <Typography variant="body1">{formatMoney(filing.totalAssets)}</Typography>
                </Grid>
              )}
              {hasValue(filing.totalLiabilities) && (
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary">
                    Total passif
                  </Typography>
                  <Typography variant="body1">{formatMoney(filing.totalLiabilities)}</Typography>
                </Grid>
              )}
              {hasValue(filing.netIncome) && (
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary">
                    Résultat net
                  </Typography>
                  <Typography variant="body1">{formatMoney(filing.netIncome)}</Typography>
                </Grid>
              )}
            </Grid>
          </CardContent>
        </Card>

        {/* Timeline */}
        <Card className="mb-4 sm:mb-5 md:mb-6">
          <CardContent sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
            <Typography variant="h6" className="mb-3 sm:mb-4 flex items-center gap-2" sx={{ fontSize: { xs: '1rem', sm: '1.1rem', md: '1.25rem' } }}>
              <CalendarTodayIcon sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }} /> Chronologie
            </Typography>
            <Divider className="mb-3 sm:mb-4" />
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="text.secondary">
                  Date de création
                </Typography>
                <Typography variant="body1">{formatParisDate(filing.createdAt)}</Typography>
              </Grid>
              {filing.submittedAt && (
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary">
                    Date de soumission
                  </Typography>
                  <Typography variant="body1">{formatParisDate(filing.submittedAt)}</Typography>
                </Grid>
              )}
              {filing.paidAt && (
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary">
                    Date de paiement
                  </Typography>
                  <Typography variant="body1">{formatParisDateTime(filing.paidAt)}</Typography>
                </Grid>
              )}
              {filing.validatedAt && (
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary">
                    Date de validation
                  </Typography>
                  <Typography variant="body1">{formatParisDateTime(filing.validatedAt)}</Typography>
                </Grid>
              )}
              {filing.rejectionReason && (
                <Grid item xs={12}>
                  <Alert severity="error">
                    <Typography variant="body2" color="text.secondary">
                      Raison du rejet
                    </Typography>
                    <Typography variant="body1">
                      {filing.rejectionReason}
                    </Typography>
                    {filing.attachmentinpi && 
                      filing.attachmentinpi.find(att => att.documentType === 'rejection_details') && (
                      <Box className="mt-3">
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<DownloadIcon />}
                          href={filing.attachmentinpi.find(att => att.documentType === 'rejection_details').fileUrl}
                          target="_blank"
                          sx={{
                            borderColor: '#d32f2f',
                            color: '#d32f2f',
                            '&:hover': {
                              borderColor: '#b71c1c',
                              backgroundColor: 'rgba(211, 47, 47, 0.04)',
                            },
                          }}
                        >
                          Télécharger les détails du rejet
                        </Button>
                      </Box>
                    )}
                  </Alert>
                </Grid>
              )}
              
              
            </Grid>
          </CardContent>
        </Card>

        {/* Documents */}
        {(filing.balanceSheetUrl ||
          filing.incomeStatementUrl ||
          filing.invoiceUrl ||
          (filing.attachments && filing.attachments.length > 0) ||
          (filing.attachmentinpi && filing.attachmentinpi.length > 0)) && (
          <Card className="mb-4 sm:mb-5 md:mb-6">
            <CardContent sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
              <Typography
                variant="h6"
                className="mb-3 sm:mb-4 flex items-center gap-2"
                sx={{ fontSize: { xs: '1rem', sm: '1.1rem', md: '1.25rem' } }}
              >
                <AttachFileIcon sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }} /> Documents
              </Typography>
              <Divider className="mb-3 sm:mb-4" />
              
              {/* Document Links */}
              <Grid container spacing={2}>
                {filing.balanceSheetUrl && (
                  <Grid item xs={12} sm={6}>
                    <Button
                      variant="outlined"
                      startIcon={<DownloadIcon />}
                      fullWidth
                      href={filing.balanceSheetUrl}
                      target="_blank"
                      sx={{
                        borderColor: '#1cbe93',
                        color: '#1cbe93',
                        fontSize: { xs: '0.875rem', sm: '1rem' },
                        padding: { xs: '8px 16px', sm: '10px 20px' },
                        '&:hover': {
                          borderColor: '#17a37e',
                          backgroundColor: 'rgba(28, 190, 147, 0.04)',
                        },
                      }}
                    >
                      Bilan
                    </Button>
                  </Grid>
                )}
                {filing.incomeStatementUrl && (
                  <Grid item xs={12} sm={6}>
                    <Button
                      variant="outlined"
                      startIcon={<DownloadIcon />}
                      fullWidth
                      href={filing.incomeStatementUrl}
                      target="_blank"
                      sx={{
                        borderColor: '#1cbe93',
                        color: '#1cbe93',
                        fontSize: { xs: '0.875rem', sm: '1rem' },
                        padding: { xs: '8px 16px', sm: '10px 20px' },
                        '&:hover': {
                          borderColor: '#17a37e',
                          backgroundColor: 'rgba(28, 190, 147, 0.04)',
                        },
                      }}
                    >
                      Compte de résultat
                    </Button>
                  </Grid>
                )}
                {filing.invoiceUrl && (
                  <Grid item xs={12} sm={6}>
                    <Button
                      variant="outlined"
                      startIcon={<DownloadIcon />}
                      fullWidth
                      href={filing.invoiceUrl}
                      target="_blank"
                      sx={{
                        borderColor: '#1cbe93',
                        color: '#1cbe93',
                        fontSize: { xs: '0.875rem', sm: '1rem' },
                        padding: { xs: '8px 16px', sm: '10px 20px' },
                        '&:hover': {
                          borderColor: '#17a37e',
                          backgroundColor: 'rgba(28, 190, 147, 0.04)',
                        },
                      }}
                    >
                      Facture ({filing.invoiceNumber})
                    </Button>
                  </Grid>
                )}
              </Grid>

              {/* Tabs for Attachments */}
              {((filing.attachments && filing.attachments.length > 0) ||
                (filing.attachmentinpi && filing.attachmentinpi.length > 0)) && (
                <Box className="mt-4">
                  <Tabs
                    value={documentTab}
                    onChange={(e, newValue) => setDocumentTab(newValue)}
                    variant="fullWidth"
                    sx={{
                      borderBottom: 1,
                      borderColor: 'divider',
                      mb: 2,
                      '& .MuiTab-root': {
                        textTransform: 'none',
                        fontWeight: 600,
                        fontSize: { xs: '0.75rem', sm: '0.875rem', md: '1rem' },
                        minHeight: { xs: '48px', sm: '56px' },
                        padding: { xs: '6px 8px', sm: '12px 16px' },
                      },
                      '& .Mui-selected': {
                        color: '#1cbe93',
                      },
                      '& .MuiTabs-indicator': {
                        backgroundColor: '#1cbe93',
                      },
                    }}
                  >
                    <Tab label="Documents téléversés" />
                    <Tab label="Documents INPI" />
                  </Tabs>

                  {/* Tab Panel 1: filing.attachments */}
                  {documentTab === 0 && (
                    <Box>
                      {filing.attachments && filing.attachments.length > 0 ? (
                        <>
                          {/* Desktop Table View */}
                          <Box className="hidden md:block">
                            <TableContainer component={Paper} sx={{ overflowX: 'auto', width: '100%' }}>
                              <Table sx={{ tableLayout: 'auto', width: '100%' }}>
                                <TableHead>
                                  <TableRow>
                                    <TableCell sx={{ minWidth: '120px' }}>Type de document</TableCell>
                                    <TableCell sx={{ minWidth: '200px', maxWidth: '300px' }}>Nom du fichier</TableCell>
                                    <TableCell sx={{ minWidth: '120px' }}>Date d'upload</TableCell>
                                    <TableCell sx={{ minWidth: '140px' }}>Actions</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {filing.attachments.map((attachment) => (
                                    <TableRow key={attachment.id}>
                                      <TableCell sx={{ wordBreak: 'break-word' }}>{labelOf(DOCUMENT_TYPE_LABELS, attachment.documentType)}</TableCell>
                                      <TableCell sx={{ wordBreak: 'break-word', maxWidth: '300px' }}>{attachment.fileName}</TableCell>
                                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                        {moment(attachment.uploadedAt).format("DD/MM/YYYY HH:mm")}
                                      </TableCell>
                                      <TableCell>
                                        {attachment.fileUrl && (
                                          <Button
                                            variant="outlined"
                                            size="small"
                                            startIcon={<DownloadIcon />}
                                            href={attachment.fileUrl}
                                            target="_blank"
                                            sx={{
                                              borderColor: '#1cbe93',
                                              color: '#1cbe93',
                                              '&:hover': {
                                                borderColor: '#17a37e',
                                                backgroundColor: 'rgba(28, 190, 147, 0.04)',
                                              },
                                            }}
                                          >
                                            Télécharger
                                          </Button>
                                        )}
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </TableContainer>
                          </Box>
                          {/* Mobile Card View */}
                          <Box className="md:hidden space-y-3">
                            {filing.attachments.map((attachment) => (
                              <div key={attachment.id} className="bg-white rounded-lg shadow-md border border-gray-200 p-4 space-y-3">
                                <div className="flex flex-col gap-1">
                                  <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Type de document</span>
                                  <span className="text-sm text-gray-900 font-medium break-words">{labelOf(DOCUMENT_TYPE_LABELS, attachment.documentType)}</span>
                                </div>
                                <div className="flex flex-col gap-1">
                                  <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Nom du fichier</span>
                                  <span className="text-sm text-gray-900 break-words">{attachment.fileName}</span>
                                </div>
                                <div className="flex flex-col gap-1">
                                  <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Date d'upload</span>
                                  <span className="text-sm text-gray-900">{moment(attachment.uploadedAt).format("DD/MM/YYYY HH:mm")}</span>
                                </div>
                                {attachment.fileUrl && (
                                  <div className="pt-3 mt-2 border-t border-gray-200">
                                    <Button
                                      variant="outlined"
                                      size="small"
                                      fullWidth
                                      startIcon={<DownloadIcon />}
                                      href={attachment.fileUrl}
                                      target="_blank"
                                      sx={{
                                        borderColor: '#1cbe93',
                                        color: '#1cbe93',
                                        '&:hover': {
                                          borderColor: '#17a37e',
                                          backgroundColor: 'rgba(28, 190, 147, 0.04)',
                                        },
                                      }}
                                    >
                                      Télécharger
                                    </Button>
                                  </div>
                                )}
                              </div>
                            ))}
                          </Box>
                        </>
                      ) : (
                        <Alert severity="info">Aucun document téléversé</Alert>
                      )}
                    </Box>
                  )}

                  {/* Tab Panel 2: filing.attachmentinpi */}
                  {documentTab === 1 && (
                    <Box>
                      {filing.attachmentinpi && filing.attachmentinpi.length > 0 ? (
                        <>
                          {/* Desktop Table View */}
                          <Box className="hidden md:block">
                            <TableContainer component={Paper} sx={{ overflowX: 'auto', width: '100%' }}>
                              <Table sx={{ tableLayout: 'auto', width: '100%' }}>
                                <TableHead>
                                  <TableRow>
                                    <TableCell sx={{ minWidth: '120px' }}>Type de document</TableCell>
                                    <TableCell sx={{ minWidth: '200px', maxWidth: '300px' }}>Nom du fichier</TableCell>
                                    <TableCell sx={{ minWidth: '120px' }}>Date d'upload</TableCell>
                                    <TableCell sx={{ minWidth: '140px' }}>Actions</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {filing.attachmentinpi.map((attachment, index) => (
                                    <TableRow key={attachment.id || index}>
                                      <TableCell sx={{ wordBreak: 'break-word' }}>{attachment.documentType ? labelOf(DOCUMENT_TYPE_LABELS, attachment.documentType) : 'N/A'}</TableCell>
                                      <TableCell sx={{ wordBreak: 'break-word', maxWidth: '300px' }}>{attachment.fileName || 'N/A'}</TableCell>
                                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                        {attachment.uploadedAt ? moment(attachment.uploadedAt).format("DD/MM/YYYY HH:mm") : 'N/A'}
                                      </TableCell>
                                      <TableCell>
                                        {attachment.fileUrl && (
                                          <Button
                                            variant="outlined"
                                            size="small"
                                            startIcon={<DownloadIcon />}
                                            href={attachment.fileUrl}
                                            target="_blank"
                                            sx={{
                                              borderColor: '#1cbe93',
                                              color: '#1cbe93',
                                              '&:hover': {
                                                borderColor: '#17a37e',
                                                backgroundColor: 'rgba(28, 190, 147, 0.04)',
                                              },
                                            }}
                                          >
                                            Télécharger
                                          </Button>
                                        )}
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </TableContainer>
                          </Box>
                          {/* Mobile Card View */}
                          <Box className="md:hidden space-y-3">
                            {filing.attachmentinpi.map((attachment, index) => (
                              <div key={attachment.id || index} className="bg-white rounded-lg shadow-md border border-gray-200 p-4 space-y-3">
                                <div className="flex flex-col gap-1">
                                  <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Type de document</span>
                                  <span className="text-sm text-gray-900 font-medium break-words">{attachment.documentType ? labelOf(DOCUMENT_TYPE_LABELS, attachment.documentType) : 'N/A'}</span>
                                </div>
                                <div className="flex flex-col gap-1">
                                  <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Nom du fichier</span>
                                  <span className="text-sm text-gray-900 break-words">{attachment.fileName || 'N/A'}</span>
                                </div>
                                <div className="flex flex-col gap-1">
                                  <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Date d'upload</span>
                                  <span className="text-sm text-gray-900">{attachment.uploadedAt ? moment(attachment.uploadedAt).format("DD/MM/YYYY HH:mm") : 'N/A'}</span>
                                </div>
                                {attachment.fileUrl && (
                                  <div className="pt-3 mt-2 border-t border-gray-200">
                                    <Button
                                      variant="outlined"
                                      size="small"
                                      fullWidth
                                      startIcon={<DownloadIcon />}
                                      href={attachment.fileUrl}
                                      target="_blank"
                                      sx={{
                                        borderColor: '#1cbe93',
                                        color: '#1cbe93',
                                        '&:hover': {
                                          borderColor: '#17a37e',
                                          backgroundColor: 'rgba(28, 190, 147, 0.04)',
                                        },
                                      }}
                                    >
                                      Télécharger
                                    </Button>
                                  </div>
                                )}
                              </div>
                            ))}
                          </Box>
                        </>
                      ) : (
                        <Alert severity="info">Aucun document INPI disponible</Alert>
                      )}
                    </Box>
                  )}
                </Box>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
}
